import React, { useState } from 'react';

import { Box, Button, DropdownMenu, HStack, Text } from '@betterment/samba-react';

import { AccountRow } from './AccountRow';
import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';
import { HouseholdAccountYtdReturnsQuery } from './graphql/query.householdAccountYtdReturns.generated';
import { HeaderCell } from './HeaderCell';

type EnvelopeAccount = HouseholdAccountsQuery['household']['accounts'][number];
type YtdReturnsData = HouseholdAccountYtdReturnsQuery['household']['accountYtdReturns'];
type ReturnType = 'twr' | 'mwr';

export const AccountsTable: React.FC<{
  accounts: EnvelopeAccount[];
  ytdReturnsData?: YtdReturnsData;
  ytdReturnsLoading?: boolean;
  showYtdReturns: boolean;
  isWeightedReturnsEnabled: boolean;
}> = ({ accounts, ytdReturnsData, ytdReturnsLoading, showYtdReturns, isWeightedReturnsEnabled }) => {
  const [returnType, setReturnType] = useState<ReturnType>('twr');

  const gridColumns = showYtdReturns
    ? 'minmax(auto, 140px) 1fr 1fr minmax(auto, 250px) minmax(auto, 190px) 32px'
    : 'minmax(auto, 140px) 1fr 1fr minmax(auto, 200px) 32px';

  return (
    <Box
      css={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gridAutoRows: 'auto',
      }}
    >
      <HeaderCell css={{ gridColumn: '1' }}>
        <Text color="text.weak">Clients</Text>
      </HeaderCell>
      <HeaderCell css={{ gridColumn: '2' }}>
        <Text color="text.weak">Account</Text>
      </HeaderCell>
      <HeaderCell css={{ gridColumn: '3' }}>
        <Text color="text.weak">Portfolio</Text>
      </HeaderCell>
      {showYtdReturns && (
        <HeaderCell css={{ gridColumn: '4', pr: 'space.300', justifyContent: 'end' }}>
          {isWeightedReturnsEnabled ? (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild={true}>
                <Button
                  variant="textPrimary"
                  css={{
                    alignItems: 'center',
                    verticalAlign: 'middle',
                    color: 'text.weak',
                    _hover: { textDecoration: 'none', color: 'text.weak' },
                  }}
                >
                  <HStack css={{ whiteSpace: 'nowrap', alignItems: 'center' }}>
                    {returnType === 'twr' ? 'Time-weighted return (YTD)' : 'Money-weighted return (YTD)'}
                    <Button.Icon name="CaretDown" />
                  </HStack>
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item onSelect={() => setReturnType('twr')}>Time-weighted return</DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => setReturnType('mwr')}>Money-weighted return</DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          ) : (
            <Text color="text.weak">Total return YTD</Text>
          )}
        </HeaderCell>
      )}
      <HeaderCell
        css={{
          gridColumn: showYtdReturns ? '5' : '4',
          justifyContent: isWeightedReturnsEnabled ? 'start' : 'end',
          pr: isWeightedReturnsEnabled ? 'space.0' : 'space.300',
        }}
      >
        <Text color="text.weak">{isWeightedReturnsEnabled ? 'Balance (Earnings YTD)' : 'Balance'}</Text>
      </HeaderCell>
      {accounts.map(account => (
        <AccountRow
          key={account.id}
          account={account}
          ytdReturnsData={ytdReturnsData}
          ytdReturnsLoading={ytdReturnsLoading}
          showYtdReturns={showYtdReturns}
          returnType={returnType}
          isWeightedReturnsEnabled={isWeightedReturnsEnabled}
        />
      ))}
    </Box>
  );
};
